import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { tokenStorage } from '../utils/crypto'

// Initial auth state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  // Workspace switching
  isWorkspaceSwitched: false,
  ownerAccountId: null,
  ownerAccountName: null,
  switchedRole: null, // The role assigned to user in the switched workspace
}

// Auth actions
const AUTH_ACTIONS = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  SWITCH_WORKSPACE: 'SWITCH_WORKSPACE',
  CLEAR_WORKSPACE: 'CLEAR_WORKSPACE',
  SET_LOADING: 'SET_LOADING',
  REFRESH_USER: 'REFRESH_USER',
}

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        isWorkspaceSwitched: false,
        ownerAccountId: null,
        ownerAccountName: null,
      }

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
      }

    case AUTH_ACTIONS.SWITCH_WORKSPACE:
      return {
        ...state,
        isWorkspaceSwitched: true,
        ownerAccountId: action.payload.ownerAccountId,
        ownerAccountName: action.payload.ownerAccountName,
        switchedRole: action.payload.switchedRole,
      }

    case AUTH_ACTIONS.CLEAR_WORKSPACE:
      return {
        ...state,
        isWorkspaceSwitched: false,
        ownerAccountId: null,
        ownerAccountName: null,
        switchedRole: null,
      }

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      }

    case AUTH_ACTIONS.REFRESH_USER:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true, // Fix: Set authenticated to true when user is refreshed
        isWorkspaceSwitched: action.payload.isWorkspaceSwitched,
        ownerAccountId: action.payload.ownerAccountId,
        ownerAccountName: action.payload.ownerAccountName,
        switchedRole: action.payload.switchedRole,
        isLoading: false,
      }

    default:
      return state
  }
}

// Create Auth Context
const AuthContext = createContext(null)

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const userInfo = tokenStorage.getUserInfo()
        
        if (userInfo && userInfo.token) {
          // Validate token by making a simple API call
          try {
            // You can uncomment this if you want to validate the token on page load
            // const response = await fetch(`${API_BASE_URL}/account-info/`, {
            //   headers: { 'Authorization': `Bearer ${userInfo.token}` }
            // })
            
            // if (response.ok) {
              dispatch({
                type: AUTH_ACTIONS.REFRESH_USER,
                payload: {
                  user: {
                    id: userInfo.id,
                    name: userInfo.name,
                    email: userInfo.email,
                    phone: userInfo.phone,
                    role: userInfo.role,
                  },
                  isWorkspaceSwitched: !!userInfo.ownerAccountId,
                  ownerAccountId: userInfo.ownerAccountId || null,
                  ownerAccountName: userInfo.ownerAccountName || null,
                  switchedRole: userInfo.switchedRole || null,
                }
              })
            // } else {
            //   // Token is invalid, clear it
            //   tokenStorage.removeUserInfo()
            //   dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
            // }
          } catch (tokenValidationError) {
            console.error('Token validation failed:', tokenValidationError)
            // For now, we'll trust the stored token and let the API interceptor handle invalid tokens
            dispatch({
              type: AUTH_ACTIONS.REFRESH_USER,
              payload: {
                user: {
                  id: userInfo.id,
                  name: userInfo.name,
                  email: userInfo.email,
                  phone: userInfo.phone,
                  role: userInfo.role,
                },
                isWorkspaceSwitched: !!userInfo.ownerAccountId,
                ownerAccountId: userInfo.ownerAccountId || null,
                ownerAccountName: userInfo.ownerAccountName || null,
                switchedRole: userInfo.switchedRole || null,
              }
            })
          }
        } else {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        // Clear potentially corrupted data
        tokenStorage.removeUserInfo()
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
      }
    }

    initializeAuth()
  }, [])

  // Auth methods
  const login = (userData, tokens) => {
    try {
      const userAuthInfo = {
        role: userData.role,
        token: tokens.access,
        name: userData.name,
        id: userData.id,
        email: userData.email,
        phone: userData.mobile,
        ownerAccountId: null,
        ownerAccountName: null,
      }

      // Store encrypted user info
      tokenStorage.setUserInfo(userAuthInfo)

      // Update auth state
      dispatch({
        type: AUTH_ACTIONS.LOGIN,
        payload: {
          user: {
            id: userData.id,
            name: userData.name,
            email: userData.email,
            phone: userData.mobile,
            role: userData.role,
          }
        }
      })

      return true
    } catch (error) {
      console.error('Login error:', error)
      return false
    }
  }

  const logout = () => {
    try {
      tokenStorage.removeUserInfo()
      dispatch({ type: AUTH_ACTIONS.LOGOUT })
      
      // Clear any cached data
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
      
      return true
    } catch (error) {
      console.error('Logout error:', error)
      return false
    }
  }

  const switchWorkspace = (ownerAccountId, ownerAccountName, switchedRole = null) => {
    try {
      // Update stored user info
      tokenStorage.setWorkspace(ownerAccountId, ownerAccountName, switchedRole)
      
      // Update auth state
      dispatch({
        type: AUTH_ACTIONS.SWITCH_WORKSPACE,
        payload: {
          ownerAccountId,
          ownerAccountName,
          switchedRole
        }
      })
      
      return true
    } catch (error) {
      console.error('Workspace switch error:', error)
      return false
    }
  }

  const clearWorkspace = () => {
    try {
      // Clear workspace from stored user info
      tokenStorage.clearWorkspace()
      
      // Update auth state
      dispatch({ type: AUTH_ACTIONS.CLEAR_WORKSPACE })
      
      return true
    } catch (error) {
      console.error('Clear workspace error:', error)
      return false
    }
  }

  const refreshUser = () => {
    try {
      const userInfo = tokenStorage.getUserInfo()
      if (userInfo && userInfo.token) {
        dispatch({
          type: AUTH_ACTIONS.REFRESH_USER,
          payload: {
            user: {
              id: userInfo.id,
              name: userInfo.name,
              email: userInfo.email,
              phone: userInfo.phone,
              role: userInfo.role,
            },
            isWorkspaceSwitched: !!userInfo.ownerAccountId,
            ownerAccountId: userInfo.ownerAccountId || null,
            ownerAccountName: userInfo.ownerAccountName || null,
            switchedRole: userInfo.switchedRole || null,
          }
        })
        return true
      }
      return false
    } catch (error) {
      console.error('Refresh user error:', error)
      return false
    }
  }

  // Context value
  const contextValue = {
    // State
    ...state,
    
    // Methods
    login,
    logout,
    switchWorkspace,
    clearWorkspace,
    refreshUser,
    
    // Utilities
    getToken: tokenStorage.getToken,
    isOwner: state.user?.role === 'user' && !state.isWorkspaceSwitched,
    canManageConnections: !state.isWorkspaceSwitched,
    getCurrentAccountContext: () => ({
      isOwnAccount: !state.isWorkspaceSwitched,
      accountId: state.isWorkspaceSwitched ? state.ownerAccountId : state.user?.id,
      accountName: state.isWorkspaceSwitched ? state.ownerAccountName : state.user?.name,
    })
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  
  return context
}

// Export auth actions for external use
export { AUTH_ACTIONS }
